//! Proactive обновление access-токена.
//!
//! Запускает таймер на `exp - REFRESH_LEAD_MS`. Когда таймер срабатывает —
//! зовёт refresh, заводит новый таймер на свежий exp. На активном юзере
//! токен всегда свежий: 401 в обычной работе исключён.
//!
//! Дополнительно:
//!   - При смене вкладки на видимую (`visibilitychange`) — если до истечения
//!     осталось меньше REFRESH_LEAD_MS, рефрешим сразу. Решает кейс
//!     sleep/hibernate, когда таймер мог быть приостановлен.
//!   - При focus окна — то же самое.
//!
//! Не дублирует логику `baseQuery`: оба используют общий `refreshAccessToken`
//! с мьютексом, никаких гонок.

import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@app/hooks';
import { store } from '@app/store';
import { adoptStoredTokens, getTokenTtlMs, refreshAccessToken } from './refresh';
import { STORAGE_KEY } from './slice';

/**
 * За сколько мс до истечения access-токена начинаем refresh.
 * 60 секунд — достаточно с запасом на сетевую задержку и часовой
 * рассинхрон между клиентом и сервером.
 */
const REFRESH_LEAD_MS = 60 * 1000;
/** Минимальная задержка между попытками refresh, чтобы не закидать
 *  сервер при странном состоянии токенов. */
const MIN_REFRESH_DELAY_MS = 1_000;

export function useProactiveTokenRefresh(): void {
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((s) => s.auth.accessToken);

  // Другое окно ОСНОВЫ (вкладка, окно листа РД) обновило пару и записало её в
  // localStorage — берём её сразу: наш refresh-токен бэкенд уже отозвал.
  useEffect(() => {
    function onStorage(e: StorageEvent): void {
      if (e.key === STORAGE_KEY && e.newValue) adoptStoredTokens(dispatch, store.getState);
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [dispatch]);

  useEffect(() => {
    if (!accessToken) return;

    let cancelled = false;
    let timerId: number | null = null;

    function scheduleNext(): void {
      if (cancelled) return;
      const ttl = getTokenTtlMs(accessToken);
      if (ttl === null) return; // токен без exp — оставляем reactive-флоу
      const delay = Math.max(MIN_REFRESH_DELAY_MS, ttl - REFRESH_LEAD_MS);
      timerId = window.setTimeout(async () => {
        // store.getState на момент срабатывания таймера — актуальное
        // состояние Redux, даже если хук был замокан давно.
        await refreshAccessToken(dispatch, store.getState);
        // refreshAccessToken обновит accessToken в стейте → useEffect
        // перезапустится с новым токеном → новый таймер. Здесь больше
        // ничего делать не надо.
      }, delay);
    }

    function refreshIfClose(): void {
      const ttl = getTokenTtlMs(accessToken);
      if (ttl === null) return;
      if (ttl <= REFRESH_LEAD_MS) {
        void refreshAccessToken(dispatch, store.getState);
      }
    }

    function onVisibility(): void {
      if (document.visibilityState === 'visible') {
        refreshIfClose();
      }
    }

    scheduleNext();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', refreshIfClose);

    return () => {
      cancelled = true;
      if (timerId !== null) window.clearTimeout(timerId);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', refreshIfClose);
    };
  }, [accessToken, dispatch]);
}
